// ============================================================
// THE COMPANY — ConversationEntry Component
// ============================================================
// NOTE: All HTML content is sanitized with DOMPurify.sanitize()
// before rendering to prevent XSS attacks.

import { useState } from 'react';
import DOMPurify from 'dompurify';
import { useCompanyStore } from '../../store/useCompanyStore';
import type { Conversation } from '../../lib/types';

interface ConversationEntryProps {
  conversation: Conversation;
}

export default function ConversationEntry({ conversation }: ConversationEntryProps) {
  const deleteConversation = useCompanyStore((s) => s.deleteConversation);
  const [hovering, setHovering] = useState(false);

  const timestamp = new Date(conversation.createdAt).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const handleDelete = () => {
    deleteConversation(conversation.id, conversation.roleId);
  };

  // Sanitize HTML content to prevent XSS
  const sanitizedAsked = DOMPurify.sanitize(conversation.asked);
  const sanitizedAnswered = DOMPurify.sanitize(conversation.answered);

  return (
    <div
      className="space-y-3 relative"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {/* Delete button — appears on hover */}
      {hovering && (
        <button
          type="button"
          onClick={handleDelete}
          className="absolute top-0 right-0 flex items-center justify-center h-5 w-5 rounded text-xs cursor-pointer transition-colors duration-150"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            color: 'var(--text-muted)',
            border: '1px solid var(--border-subtle)',
          }}
          title="Delete conversation"
        >
          &times;
        </button>
      )}

      {/* "Asked" bubble — right-aligned, accent tint */}
      <div className="flex justify-end">
        <div
          className="max-w-[85%] rounded-xl rounded-tr-sm px-3.5 py-2.5 text-sm"
          style={{
            backgroundColor: 'rgba(99, 102, 241, 0.15)',
            border: '1px solid rgba(99, 102, 241, 0.25)',
            color: 'var(--text-primary)',
          }}
        >
          {/* Content is sanitized via DOMPurify.sanitize() above */}
          <div
            className="prose-compact"
            dangerouslySetInnerHTML={{ __html: sanitizedAsked }}
          />
        </div>
      </div>

      {/* "Answered" bubble — left-aligned, surface background */}
      <div className="flex justify-start">
        <div
          className="max-w-[85%] rounded-xl rounded-tl-sm px-3.5 py-2.5 text-sm"
          style={{
            backgroundColor: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
            color: 'var(--text-primary)',
          }}
        >
          {/* Content is sanitized via DOMPurify.sanitize() above */}
          <div
            className="prose-compact"
            dangerouslySetInnerHTML={{ __html: sanitizedAnswered }}
          />
        </div>
      </div>

      {/* Timestamp */}
      <p
        className="text-[11px] text-center"
        style={{ color: 'var(--text-muted)' }}
      >
        {timestamp}
      </p>
    </div>
  );
}
